// Phone cameras produce 4-12 MB photos, over the 5 MB upload limit and far more detail than
// a shelf photo needs. Shrink large photos to at most MAX_EDGE px on the long edge as JPEG.
// 2400px stays inside what the AI reads at full resolution (2576px), so bins stay sharp.
const MAX_EDGE = 2400;
const SMALL_ENOUGH_BYTES = 1.5 * 1024 * 1024;
const JPEG_QUALITY = 0.86;

// maxEdge: a smaller limit for images that are only ever shown small (product photos)
export async function prepareImageForUpload(file, { maxEdge = MAX_EDGE } = {}) {
  if (!file || !file.type.startsWith('image/') || file.type === 'image/gif') return file;

  let bitmap;
  try {
    // 'from-image' applies the camera's EXIF rotation, so portrait photos stay upright
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // the browser can't decode it; upload as is and let the server decide
  }

  const longEdge = Math.max(bitmap.width, bitmap.height);
  if (longEdge <= maxEdge && file.size <= SMALL_ENOUGH_BYTES) {
    bitmap.close();
    return file;
  }

  const scale = Math.min(1, maxEdge / longEdge);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob) return file;
  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg' });
}
